<template>
    <v-container fluid>
        <v-row justify="center">
            <v-card width="90%" class="mt-5" elevation="0" style="background-color: #f4f4f5 !important">
                <v-card-title class="text-h5">
                    <v-btn icon class="mr-2" @click="$router.push({ name: '/admin_blog' })">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <strong style="font-size:26px;">{{ is_new ? 'New blog post' : 'Edit blog post' }}</strong>
                    <v-chip v-if="!is_new" small class="ml-3" :color="saved_status == 'published' ? 'green' : 'grey'" dark>
                        {{ saved_status == 'published' ? 'Published' : 'Draft' }}
                    </v-chip>
                    <v-spacer />
                    <v-btn v-if="saved_status == 'published'" text color="#172C54" style="text-transform: none !important"
                        :href="live_url" target="_blank">
                        View live <v-icon small class="ml-1">mdi-open-in-new</v-icon>
                    </v-btn>
                </v-card-title>
            </v-card>
        </v-row>

        <v-row v-if="!loading" style="width: 90%; margin: 0 auto">
            <v-col cols="12" md="8">
                <v-card elevation="0" style="border-radius: 16px" class="pa-4">
                    <strong>Title *</strong>
                    <v-text-field v-model="post.title" filled dense placeholder="e.g. How to Plan a Funeral in Ireland"
                        @input="onTitleInput" />

                    <strong>Content</strong>
                    <p class="hint">Use "Heading 2" for main sections and "Heading 3" for sub-sections; it helps Google understand the article.</p>
                    <vue-editor v-model="post.content" :editor-toolbar="toolbar" use-custom-image-handler
                        @image-added="onEditorImage" class="blog_editor" />
                </v-card>
            </v-col>

            <v-col cols="12" md="4">
                <v-card elevation="0" style="border-radius: 16px" class="pa-4 mb-4">
                    <div class="d-flex flex-wrap" style="gap: 8px">
                        <v-btn outlined color="#172C54" style="text-transform: none !important" :loading="saving == 'draft'"
                            :disabled="!!saving" @click="save('draft')">
                            {{ saved_status == 'published' ? 'Unpublish' : 'Save draft' }}
                        </v-btn>
                        <v-btn outlined color="#172C54" style="text-transform: none !important" @click="preview()">
                            Preview
                        </v-btn>
                        <v-btn dark color="#172C54" style="text-transform: none !important" :loading="saving == 'published'"
                            :disabled="!!saving" @click="save('published')">
                            {{ saved_status == 'published' ? 'Update' : 'Publish' }}
                        </v-btn>
                    </div>
                    <p class="hint mt-3 mb-0">Publishing puts the post live on myfarewellwishes.ie/blog straight away.</p>
                </v-card>

                <v-card elevation="0" style="border-radius: 16px" class="pa-4 mb-4">
                    <strong>Featured image</strong>
                    <p class="hint">Shown at the top of the post, on the blog page and when shared on social media. Landscape, at least 1200px wide.</p>
                    <v-img v-if="post.featured_image" :src="post.featured_image" max-height="180" contain class="mb-2" />
                    <div class="d-flex" style="gap: 8px">
                        <v-btn small outlined :loading="uploading" @click="$refs.featured_input.click()" style="text-transform: none !important">
                            {{ post.featured_image ? 'Change image' : 'Upload image' }}
                        </v-btn>
                        <v-btn v-if="post.featured_image" small text color="red" @click="post.featured_image = null" style="text-transform: none !important">
                            Remove
                        </v-btn>
                    </div>
                    <input ref="featured_input" type="file" accept="image/jpeg,image/png,image/webp" style="display:none"
                        @change="onFeaturedSelected" />
                    <v-text-field v-if="post.featured_image" v-model="post.featured_image_alt" class="mt-3" filled dense
                        label="Image description (alt text)" hint="Describe the image for screen readers and Google" persistent-hint />
                </v-card>

                <v-card elevation="0" style="border-radius: 16px" class="pa-4 mb-4">
                    <strong>SEO</strong>
                    <div class="serp mt-2 mb-4">
                        <div class="serp_url">myfarewellwishes.ie › blog › {{ post.slug || slugify(post.title) || 'your-post' }}</div>
                        <div class="serp_title">{{ seo_title || 'Post title' }} | My Farewell Wishes</div>
                        <div class="serp_desc">{{ post.excerpt || 'Add a meta description below so you control what Google shows here.' }}</div>
                    </div>

                    <v-text-field v-model="post.slug" filled dense label="URL slug" prefix="/blog/"
                        :hint="slug_hint" persistent-hint @input="slug_touched = true" />
                    <v-text-field v-model="post.meta_title" class="mt-3" filled dense label="SEO title (optional)"
                        :counter="60" hint="Defaults to the post title. Aim for under 60 characters." persistent-hint />
                    <v-textarea v-model="post.excerpt" class="mt-3" filled dense rows="3" auto-grow label="Meta description"
                        :counter="160" hint="1-2 sentences summarising the post. Shown in Google and on the blog page."
                        persistent-hint />

                    <div class="mt-4">
                        <div v-for="(c, i) in seo_checks" :key="i" class="check">
                            <v-icon small :color="c.ok ? 'green' : 'orange'">{{ c.ok ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                            <span>{{ c.label }}</span>
                        </div>
                    </div>
                </v-card>

                <v-card elevation="0" style="border-radius: 16px" class="pa-4 mb-4">
                    <v-text-field v-model="post.author_name" filled dense label="Author (optional)"
                        hint="Leave blank for 'The My Farewell Wishes Team'" persistent-hint />
                    <v-text-field v-model="published_date" class="mt-3" filled dense type="date" label="Publish date"
                        hint="Leave blank to use the date you publish" persistent-hint />
                </v-card>

                <v-btn v-if="!is_new" text color="red" style="text-transform: none !important" @click="delete_dialog = true">
                    <v-icon small class="mr-1">mdi-delete</v-icon> Delete post
                </v-btn>
            </v-col>
        </v-row>

        <v-dialog v-model="delete_dialog" max-width="420">
            <v-card>
                <v-card-title>Delete this post?</v-card-title>
                <v-card-text>It will be removed from the website straight away.</v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="delete_dialog = false">Cancel</v-btn>
                    <v-btn color="red" dark @click="deletePost()">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { VueEditor } from 'vue2-editor'

export default {
    components: { VueEditor },
    data: () => ({
        loading: false,
        saving: null,
        uploading: false,
        delete_dialog: false,
        slug_touched: false,
        saved_status: null,
        saved_slug: null,
        original_date: null,
        published_date: null,
        post: {
            id: null,
            title: '',
            slug: '',
            meta_title: '',
            excerpt: '',
            content: '',
            featured_image: null,
            featured_image_alt: '',
            author_name: '',
        },
        toolbar: [
            [{ header: [false, 2, 3, 4] }],
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['blockquote', 'link', 'image', 'video'],
            [{ align: '' }, { align: 'center' }],
            ['clean'],
        ],
    }),
    computed: {
        is_new() {
            return !this.$route.params.id
        },
        live_url() {
            return 'https://myfarewellwishes.ie/blog/' + this.saved_slug + '/'
        },
        seo_title() {
            return this.post.meta_title || this.post.title
        },
        slug_hint() {
            if (this.saved_status == 'published' && this.post.slug != this.saved_slug) {
                return 'Warning: changing the URL of a published post breaks existing links to it.'
            }
            return 'Short, lowercase, words separated by hyphens.'
        },
        word_count() {
            const text = (this.post.content || '').replace(/<[^>]+>/g, ' ').trim()
            return text ? text.split(/\s+/).length : 0
        },
        seo_checks() {
            const title_len = (this.seo_title || '').length
            const desc_len = (this.post.excerpt || '').length
            return [
                { ok: title_len >= 20 && title_len <= 60, label: 'SEO title is 20-60 characters (' + title_len + ')' },
                { ok: desc_len >= 70 && desc_len <= 160, label: 'Meta description is 70-160 characters (' + desc_len + ')' },
                { ok: this.word_count >= 600, label: 'At least 600 words (' + this.word_count + ')' },
                { ok: /<h2/i.test(this.post.content || ''), label: 'Uses at least one Heading 2' },
                { ok: /href="(https:\/\/myfarewellwishes\.ie)?\//i.test(this.post.content || ''), label: 'Links to another page on the site' },
                { ok: !!this.post.featured_image && !!this.post.featured_image_alt, label: 'Featured image with alt text' },
            ]
        },
    },
    async mounted() {
        if (!this.is_new) {
            await this.getPost()
        }
    },
    methods: {
        showSnackBar(message) {
            this.$store.commit('auth/setMessage', { show: true, message: message }, { root: 1 })
        },
        errorMessage(data, fallback) {
            if (data && data.errors) {
                return Object.values(data.errors)[0][0]
            }
            return (data && data.message) || fallback
        },
        slugify(text) {
            return (text || '').toLowerCase()
                .normalize('NFD').replace(/[̀-ͯ]/g, '')
                .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
                .substring(0, 120)
        },
        onTitleInput() {
            // Keep the slug following the title until it is published or edited by hand.
            if (!this.slug_touched && this.saved_status != 'published') {
                this.post.slug = this.slugify(this.post.title)
            }
        },
        async getPost() {
            this.loading = true
            const { data } = await this.$axios.get('auth/admin/blog/posts/' + this.$route.params.id)
            this.loading = false
            if (!data || !data.response) {
                this.showSnackBar(this.errorMessage(data, 'Could not load post'))
                this.$router.push({ name: '/admin_blog' })
                return
            }
            this.applyPost(data.data)
        },
        applyPost(p) {
            Object.keys(this.post).forEach(k => {
                this.post[k] = p[k] == null ? (k == 'featured_image' || k == 'id' ? null : '') : p[k]
            })
            this.saved_status = p.status
            this.saved_slug = p.slug
            this.slug_touched = true
            this.published_date = p.published_at ? p.published_at.substring(0, 10) : null
            this.original_date = this.published_date
        },
        payload(status) {
            const body = { ...this.post, status: status }
            delete body.id
            if (!body.slug) delete body.slug
            if (this.published_date && this.published_date != this.original_date) {
                body.published_at = this.published_date
            }
            return body
        },
        async save(status) {
            if (!this.post.title) {
                this.showSnackBar('Please add a title')
                return
            }
            this.saving = status
            const { data } = this.is_new
                ? await this.$axios.post('auth/admin/blog/posts', this.payload(status))
                : await this.$axios.patch('auth/admin/blog/posts/' + this.post.id, this.payload(status))
            this.saving = null

            if (data && data.data) {
                this.applyPost(data.data)
                if (this.is_new) {
                    this.$router.replace({ name: '/admin_blog_edit', params: { id: data.data.id } })
                }
            }
            if (data && data.response) {
                this.showSnackBar(status == 'published' ? 'Published: the post is live on the website' : 'Saved as draft')
            } else {
                this.showSnackBar(this.errorMessage(data, 'Could not save the post'))
            }
        },
        async preview() {
            if (!this.post.title) {
                this.showSnackBar('Please add a title')
                return
            }
            // Open the tab before the request so popup blockers allow it.
            const win = window.open('', '_blank')
            const body = this.payload('draft')
            body.id = this.post.id
            body.published_at = this.published_date || undefined
            const { data } = await this.$axios.post('auth/admin/blog/preview', body)
            if (data && data.response && win) {
                win.document.open()
                win.document.write(data.html)
                win.document.close()
            } else {
                if (win) win.close()
                this.showSnackBar(this.errorMessage(data, 'Could not build the preview'))
            }
        },
        async uploadImage(file) {
            const formData = new FormData()
            formData.append('image', file)
            const { data } = await this.$axios.post('auth/admin/blog/upload_image', formData)
            if (data && data.response) {
                return data.url
            }
            this.showSnackBar(this.errorMessage(data, 'Image upload failed'))
            return null
        },
        async onFeaturedSelected(e) {
            const file = e.target.files[0]
            e.target.value = ''
            if (!file) return
            this.uploading = true
            const url = await this.uploadImage(file)
            this.uploading = false
            if (url) this.post.featured_image = url
        },
        async onEditorImage(file, Editor, cursorLocation, resetUploader) {
            const url = await this.uploadImage(file)
            if (url) {
                Editor.insertEmbed(cursorLocation, 'image', url)
            }
            resetUploader()
        },
        async deletePost() {
            this.delete_dialog = false
            try {
                const { data } = await this.$axios.delete('auth/admin/blog/posts/' + this.post.id)
                this.showSnackBar((data && data.message) || 'Post deleted')
                this.$router.push({ name: '/admin_blog' })
            } catch (err) {
                this.showSnackBar(this.errorMessage(err && err.data, 'Could not delete the post'))
            }
        },
    },
}
</script>

<style scoped>
.hint {
    font-size: 12px;
    color: #777;
    margin-bottom: 8px;
}
.blog_editor ::v-deep .ql-editor {
    min-height: 480px;
    font-size: 16px;
    line-height: 1.7;
}
.blog_editor ::v-deep .ql-editor img {
    max-width: 100%;
}
.serp {
    border: 1px solid #e3e3e3;
    border-radius: 8px;
    padding: 12px;
    font-family: Arial, sans-serif;
}
.serp_url {
    font-size: 12px;
    color: #4d5156;
}
.serp_title {
    font-size: 18px;
    color: #1a0dab;
    line-height: 1.3;
    margin: 2px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.serp_desc {
    font-size: 13px;
    color: #4d5156;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.check {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    margin-bottom: 4px;
}
</style>
